import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { firmFromQS } from "@/lib/nav";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { RISK_META, RISK_FALLBACK } from "@/lib/risk";
import { TierBadge, PocChip, PocChipPair, UsersCell } from "@/components/Badges";
import { FirmSubmeta } from "@/components/FirmSubmeta";
import RulesInfoDialog from "@/components/RulesInfoDialog";
import CSMetricsView from "@/components/CSMetricsView";
import ExportButton from "@/components/ExportButton";
import { CUSTOMER_NEW_COLUMNS } from "@/lib/exportColumns";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  MagnifyingGlass, CaretRight, Plant, Warning, Pulse, Trophy, BookOpen, Lightning,
} from "@phosphor-icons/react";
import FirmRowActions from "@/components/FirmRowActions";

const CLASS_META = {
  Active:    { color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200", dot: "bg-emerald-500", barClass: "[&>div]:bg-emerald-500" },
  "At Risk": { color: "text-amber-700",   bg: "bg-amber-50 border-amber-200",     dot: "bg-amber-500",   barClass: "[&>div]:bg-amber-500" },
  Inactive:  { color: "text-orange-700",  bg: "bg-orange-50 border-orange-200",   dot: "bg-orange-500",  barClass: "[&>div]:bg-orange-500" },
};

const SEGMENT_META = {
  onboarding:  { label: "Just Onboarded",  subtitle: "0–4 months"  },
  adopting:    { label: "Adopting",        subtitle: "4–8 months"  },
  pre_renewal: { label: "Pre-Renewal",     subtitle: "8–12 months" },
};

const FALLBACK_META = { color: "text-gray-700", bg: "bg-gray-50 border-gray-200", dot: "bg-gray-400", barClass: "[&>div]:bg-gray-400" };

const ALL_BUCKETS = ["Active", "At Risk", "Inactive"];

const SIGNAL_META = {
  s1: { icon: BookOpen, name: "Data Import Depth" },
  s2: { icon: Trophy, name: "Statement Generation (AHA)" },
  s3: { icon: Lightning, name: "Assignment Velocity" },
  s4: { icon: Pulse, name: "Recent Momentum" },
  s5: { icon: Pulse, name: "Ledger Scrutiny" },
  s6: { icon: Trophy, name: "Closure Signal" },
};

export default function NewCustomers() {
  const { user } = useAuth();
  const isPoc = user?.role === "poc";
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab") || "list";
  const tab = rawTab === "funnel" ? "list" : rawTab;
  const setTab = (t) => {
    const next = new URLSearchParams(searchParams);
    if (t === "list") next.delete("tab");
    else next.set("tab", t);
    setSearchParams(next, { replace: true });
  };

  const filters = {
    search: searchParams.get("search") || "",
    tier: searchParams.get("tier") || "all",
    poc: searchParams.get("poc") || "all",
    classification: searchParams.get("classification") || "all",
    segment: searchParams.get("segment") || "all",
  };
  const setFilter = (k, v) => {
    const next = new URLSearchParams(searchParams);
    if (!v || v === "all" || v === "") next.delete(k);
    else next.set(k, v);
    setSearchParams(next, { replace: false });
  };

  const [searchInput, setSearchInput] = useState(filters.search);
  const [customers, setCustomers] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [pocOptions, setPocOptions] = useState([]);
  const [tierOptions, setTierOptions] = useState([]);

  useEffect(() => {
    api.get("/meta/options").then((r) => {
      setPocOptions(r.data.pocs);
      setTierOptions(r.data.tiers);
    });
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== filters.search) setFilter("search", searchInput);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [searchInput]);

  useEffect(() => {
    setLoading(true);
    api
      .get("/customers/new", {
        params: {
          search: filters.search || undefined,
          tier: filters.tier !== "all" ? filters.tier : undefined,
          poc: filters.poc !== "all" ? filters.poc : undefined,
          classification: filters.classification !== "all" ? filters.classification : undefined,
          segment: filters.segment !== "all" ? filters.segment : undefined,
        },
      })
      .then((r) => {
        setCustomers(r.data.customers);
        setCounts(r.data.counts);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [filters.search, filters.tier, filters.poc, filters.classification, filters.segment]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-gray-900 flex items-center gap-2">
          <Plant size={28} weight="fill" className="text-emerald-600" />
          New Customers
        </h1>
        <p className="text-sm text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
          <span>Firms activated in the last 365 days, grouped by tenure stage.</span>
          <RulesInfoDialog view="new_customers" label="New Customers" />
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="nc-segment-cards">
        <AllSegmentsCard
          counts={counts}
          active={filters.segment === "all"}
          onClick={() => setFilter("segment", "all")}
        />
        <SegmentCard
          segKey="onboarding"
          counts={counts}
          active={filters.segment === "onboarding"}
          onClick={() => setFilter("segment", filters.segment === "onboarding" ? "all" : "onboarding")}
        />
        <SegmentCard
          segKey="adopting"
          counts={counts}
          active={filters.segment === "adopting"}
          onClick={() => setFilter("segment", filters.segment === "adopting" ? "all" : "adopting")}
        />
        <SegmentCard
          segKey="pre_renewal"
          counts={counts}
          active={filters.segment === "pre_renewal"}
          onClick={() => setFilter("segment", filters.segment === "pre_renewal" ? "all" : "pre_renewal")}
        />
      </div>

      <div className="flex items-center gap-1 border-b border-gray-200 justify-between" data-testid="nc-tabs">
        <div className="flex items-center gap-1">
          <TabBtn active={tab === "list"} onClick={() => setTab("list")} testid="nc-tab-list">
            Customer list
          </TabBtn>
          <TabBtn active={tab === "score"} onClick={() => setTab("score")} testid="nc-tab-score">
            Health Score Card
          </TabBtn>
          <TabBtn active={tab === "metrics"} onClick={() => setTab("metrics")} testid="nc-tab-metrics">
            CS Metrics
          </TabBtn>
        </div>
        {(tab === "list" || tab === "score") && (
          <div className="pb-2">
            <ExportButton
              filename="new-customers"
              sheetName="New Customers"
              columns={CUSTOMER_NEW_COLUMNS}
              rows={customers}
              testId="export-new-customers"
            />
          </div>
        )}
      </div>

      {(tab === "list" || tab === "score") && (
        <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
              data-testid="nc-search"
            />
          </div>
          <Select value={filters.tier} onValueChange={(v) => setFilter("tier", v)}>
            <SelectTrigger className="w-[150px]" data-testid="nc-filter-tier"><SelectValue placeholder="All tiers" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tiers</SelectItem>
              {tierOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.poc} onValueChange={(v) => setFilter("poc", v)}>
            <SelectTrigger className="w-[160px]" data-testid="nc-filter-poc"><SelectValue placeholder="All POCs" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All POCs</SelectItem>
              {pocOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.classification} onValueChange={(v) => setFilter("classification", v)}>
            <SelectTrigger className="w-[200px]" data-testid="nc-filter-class"><SelectValue placeholder="All buckets" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All buckets</SelectItem>
              {ALL_BUCKETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {tab === "list" && <CustomerListView customers={customers} loading={loading} />}
      {tab === "score" && <HealthScoreCardView customers={customers} loading={loading} />}
      {tab === "metrics" && <CSMetricsView />}
    </div>
  );
}

function SegmentCard({ segKey, counts, active = false, onClick }) {
  const meta = SEGMENT_META[segKey];
  const buckets = counts?.[segKey] || {};
  const total = counts?._total_per_segment?.[segKey] || 0;
  const good = buckets.Active || 0;
  const mid = buckets["At Risk"] || 0;
  const risk = buckets.Inactive || 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`bg-white border rounded-lg p-4 text-left transition-all hover:border-gray-300 hover:shadow-sm ${
        active ? "border-gray-900 ring-2 ring-gray-900/10" : "border-gray-200"
      }`}
      data-testid={`nc-segment-card-${segKey}`}
      aria-pressed={active}
    >
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] text-gray-500 font-medium">
            {meta.label}
          </div>
          <div className="text-[10px] text-gray-400 mt-0.5">{meta.subtitle}</div>
        </div>
        <div className="font-display text-[28px] leading-none font-semibold text-gray-900 tabular-nums">
          {total}
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3 text-xs">
        <span className="inline-flex items-center gap-1 text-emerald-700"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />{good}</span>
        <span className="inline-flex items-center gap-1 text-amber-700"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />{mid}</span>
        <span className="inline-flex items-center gap-1 text-orange-700"><span className="w-1.5 h-1.5 rounded-full bg-orange-500" />{risk}</span>
      </div>
      <div className="mt-1 text-[10px] text-gray-400">
        Active · At Risk · Inactive
      </div>
    </button>
  );
}

function AllSegmentsCard({ counts, active = false, onClick }) {
  const per = counts?._total_per_segment || {};
  const total = (per.onboarding || 0) + (per.adopting || 0) + (per.pre_renewal || 0);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`bg-white border rounded-lg p-4 text-left transition-all hover:border-gray-300 hover:shadow-sm ${
        active ? "border-gray-900 ring-2 ring-gray-900/10" : "border-gray-200"
      }`}
      data-testid="nc-segment-card-all"
      aria-pressed={active}
    >
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] text-gray-500 font-medium">
            All New
          </div>
          <div className="text-[10px] text-gray-400 mt-0.5">0–12 months</div>
        </div>
        <div className="font-display text-[28px] leading-none font-semibold text-gray-900 tabular-nums">
          {total}
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3 text-xs text-gray-600">
        <span className="tabular-nums">{per.onboarding || 0}</span>
        <span className="text-gray-300">·</span>
        <span className="tabular-nums">{per.adopting || 0}</span>
        <span className="text-gray-300">·</span>
        <span className="tabular-nums">{per.pre_renewal || 0}</span>
      </div>
      <div className="mt-1 text-[10px] text-gray-400">
        Just Onboarded · Adopting · Pre-Renewal
      </div>
    </button>
  );
}

function SummaryCard({ label, value, dotClass }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="text-[11px] uppercase tracking-[0.15em] text-gray-500 font-medium flex items-center gap-2">
        <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} />
        {label}
      </div>
      <div className="mt-2 font-display text-[28px] leading-none font-semibold text-gray-900 tabular-nums">
        {value}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      className={`pb-2.5 px-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
        active ? "border-gray-900 text-gray-900" : "border-transparent text-gray-500 hover:text-gray-700"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function ClassBadge({ value }) {
  const m = CLASS_META[value] || FALLBACK_META;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium border whitespace-nowrap ${m.bg} ${m.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
      {value}
    </span>
  );
}

function ScoreBar({ score, classification, showNumber = true }) {
  const m = CLASS_META[classification] || FALLBACK_META;
  return (
    <div className="flex items-center gap-2 min-w-[140px]">
      <Progress value={score} className={`h-1.5 flex-1 ${m.barClass}`} />
      {showNumber && (
        <span className="text-xs font-mono tabular-nums text-gray-700 w-9 text-right">{score}</span>
      )}
    </div>
  );
}

function CustomerListView({ customers, loading }) {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="nc-list">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr className="text-left">
            <Th>Firm</Th><Th>Day</Th><Th>Users</Th><Th>Tier</Th><Th>Bucket</Th><Th>POC</Th><th className="w-16"></th><th className="w-12"></th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-400">Loading…</td></tr>}
          {!loading && customers.length === 0 && (
            <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-400">No new customers in this window.</td></tr>
          )}
          {!loading && customers.map((c) => {
            const meta = RISK_META[c.risk] || RISK_FALLBACK;
            return (
            <tr
              key={c.subdomain}
              onClick={() => navigate(`/firms/${c.subdomain}${firmFromQS(location)}`)}
              className={`border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer ${meta.leftClass}`}
              data-testid={`nc-row-${c.subdomain}`}
            >
              <td className="px-4 py-3">
                <div className="font-medium text-gray-900">{c.firm_name}</div>
                <FirmSubmeta firm={c} />
              </td>
              <td className="px-4 py-3 font-mono tabular-nums text-gray-700">{c.days_active}</td>
              <td className="px-4 py-3"><UsersCell value={c.num_users} /></td>
              <td className="px-4 py-3"><TierBadge tier={c.tier} /></td>
              <td className="px-4 py-3"><ClassBadge value={c.bucket} /></td>
              <td className="px-4 py-3"><PocChipPair poc={c.poc} salesPoc={c.sales_poc} /></td>
              <td className="px-2 py-3 whitespace-nowrap"><FirmRowActions firm={c} /></td>
              <td className="px-4 py-3 text-right"><CaretRight size={16} className="text-gray-400" /></td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium text-gray-500">{children}</th>;
}

function HealthScoreCardView({ customers, loading }) {
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    // Auto-select the most concerning (lowest score)
    if (!selected && customers.length > 0) {
      setSelected(customers[0].subdomain);
    }
    // eslint-disable-next-line
  }, [customers]);

  const flagged = customers.filter((c) => c.bucket === "Inactive");
  const selectedCustomer = customers.find((c) => c.subdomain === selected);

  return (
    <div className="space-y-6" data-testid="nc-score-view">
      {flagged.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="nc-immediate-flags">
          <Warning size={20} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-red-900 text-sm">
              {flagged.length} new customer{flagged.length > 1 ? "s" : ""} need immediate intervention
            </div>
            <div className="text-xs text-red-800 mt-0.5 leading-relaxed">
              {flagged.slice(0, 3).map((c) => c.firm_name).join(", ")}
              {flagged.length > 3 ? ` + ${flagged.length - 3} more` : ""}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="nc-score-list">
          <div className="px-3 py-2 border-b border-gray-100 text-[11px] uppercase tracking-wider text-gray-500 font-medium">
            All new customers · ranked by score
          </div>
          <div className="max-h-[640px] overflow-y-auto">
            {loading && <div className="p-4 text-sm text-gray-400">Loading…</div>}
            {!loading && customers.map((c) => (
              <button
                key={c.subdomain}
                onClick={() => setSelected(c.subdomain)}
                className={`w-full text-left px-3 py-2.5 border-b border-gray-100 last:border-b-0 transition-colors ${
                  selected === c.subdomain ? "bg-gray-50" : "hover:bg-gray-50"
                }`}
                data-testid={`nc-score-item-${c.subdomain}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium text-gray-900 truncate">{c.firm_name}</div>
                  <span className="font-mono text-xs tabular-nums text-gray-700">{c.health_score}</span>
                </div>
                <div className="mt-1.5"><ScoreBar score={c.health_score} classification={c.bucket} showNumber={false} /></div>
                <div className="text-[10px] text-gray-500 mt-1">Day {c.days_active} · {c.poc} · {c.tier}</div>
              </button>
            ))}
          </div>
        </div>

        {selectedCustomer ? (
          <CustomerScoreDetail subdomain={selectedCustomer.subdomain} />
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center text-sm text-gray-400">
            Select a customer to view their score breakdown.
          </div>
        )}
      </div>
    </div>
  );
}

function CustomerScoreDetail({ subdomain }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/customers/new/${subdomain}/health-score`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [subdomain]);

  if (loading || !data) {
    return <div className="bg-white border border-gray-200 rounded-lg p-12 text-center text-sm text-gray-400">Loading…</div>;
  }

  const { firm, score } = data;
  const m = CLASS_META[score.classification] || FALLBACK_META;

  return (
    <div className="space-y-5" data-testid="nc-score-detail">
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-xl font-semibold text-gray-900">{firm.firm_name}</h3>
            <div className="text-xs text-gray-500 mt-1 flex flex-wrap items-center gap-2">
              <span className="font-mono">{firm.firm_url}</span>
              <span className="text-gray-300">·</span>
              <span>Day {score.days_active}</span>
              <span className="text-gray-300">·</span>
              <TierBadge tier={firm.tier} />
              <PocChip name={firm.poc} showName />
              {firm.sales_poc ? (
                <>
                  <span className="text-gray-300">·</span>
                  <PocChip name={firm.sales_poc} variant="sales" showName />
                </>
              ) : null}
            </div>
          </div>
          <div className="text-right">
            <div className="font-display text-[40px] leading-none font-semibold text-gray-900 tabular-nums">
              {score.total}
            </div>
            <div className="mt-2"><ClassBadge value={score.classification} /></div>
          </div>
        </div>
        <div className="mt-4">
          <Progress value={score.total} className={`h-2 ${m.barClass}`} />
        </div>
        <div className="mt-4 flex justify-end">
          <Button variant="outline" size="sm" onClick={() => navigate(`/firms/${firm.subdomain}${firmFromQS(location)}`)} data-testid="nc-view-360-btn">
            View full 360°
            <CaretRight size={14} className="ml-1" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {Object.entries(score.signals).map(([k, s]) => {
          const Icon = SIGNAL_META[k]?.icon || Pulse;
          const gated = s.gated;
          const pct = gated ? 0 : (s.score / s.max) * 100;
          const filled = !gated && s.score > 0;
          return (
            <div key={k} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`signal-${k}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Icon size={16} className={filled ? "text-emerald-600" : "text-gray-400"} weight={filled ? "fill" : "regular"} />
                  <span className="text-sm font-semibold text-gray-900">{s.label}</span>
                </div>
                <span className="font-mono text-sm tabular-nums text-gray-700">
                  {gated ? "—" : `${s.score}`}<span className="text-gray-400">/{s.max}</span>
                </span>
              </div>
              <div className="mt-2.5">
                <Progress value={pct} className="h-1.5 [&>div]:bg-gray-900" />
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {gated ? s.note : <><span className="font-mono">{s.value}</span> {s.unit}</>}
              </div>
            </div>
          );
        })}
      </div>

      <AlgorithmSpec />
    </div>
  );
}

function AlgorithmSpec() {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="algo-spec">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-gray-50"
      >
        <span className="font-display text-sm font-semibold text-gray-700 uppercase tracking-wider">
          Algorithm spec
        </span>
        <CaretRight size={14} className={`transition-transform text-gray-400 ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="px-4 py-4 border-t border-gray-100 text-xs leading-relaxed text-gray-700 space-y-3">
          <p>
            6 signals · max 100 points · the badge above shows one of three buckets —
            Active / At Risk / Inactive — derived from the score band (≥ 65 / 35–64 / &lt; 35)
            with a freshness override: a high score is demoted if the firm hasn't been seen in &gt; 30 days.
            The same vocabulary is used across all three tenure segments (Just Onboarded, Adopting, Pre-Renewal)
            but the underlying signals are weighted differently.
            S6 (Closure Signal) is gated until day 21 — in the first 20 days the score is computed out of 85 and scaled to 100.
          </p>
          <ul className="space-y-1.5 list-disc pl-5">
            <li><strong>S1 Data Import Depth (20):</strong> total_vouchers / days_active · cap at 30/day</li>
            <li><strong>S2 Statement Generation (20):</strong> SG events in 365D · 5+ events = 20, 3+ = 14, 1+ = 8</li>
            <li><strong>S3 Assignment Velocity (20):</strong> total_assignments / weeks_active · cap at 5/week</li>
            <li><strong>S4 Recent Momentum (15):</strong> Mixpanel events in last 30 days · cap at 100 events</li>
            <li><strong>S5 Ledger Scrutiny (10):</strong> binary — 10 if at least 1 LS event in 365D, else 0</li>
            <li><strong>S6 Closure Signal (15):</strong> binary — 15 if any closed assignment, else 0. Skipped before day 21.</li>
          </ul>
        </div>
      )}
    </div>
  );
}
